<template>
  <BittsLoading :is-loading="isInitialLoading">
    <div
      class="credit-usage-client-table"
      data-testid="credit-usage-client-table"
    >
      <BittsEmptyState
        v-if="!rows.length"
        :font-awesome-icon="['far', 'robot']"
        :font-awesome-icon-style="{ height: '26px', width: '26px' }"
        font-awesome-icon-class="empty-state-icon"
        title="No client usage yet"
        description="Clients will appear here once they start using credits."
      />
      <BittsTablePro
        v-else
        :columns="columns"
        :rows="rows"
        :row-height="44"
        :pagination="rows.length > 10"
        :pagination-page-size="10"
        :suppress-movable-columns="true"
        :always-show-horizontal-scroll="false"
      />
    </div>
  </BittsLoading>
</template>

<script setup lang="ts">
import { BittsEmptyState, BittsLoading, BittsTablePro } from '@crossbeam/bitts';

import { ColDef } from '@ag-grid-community/core';
import { computed } from 'vue';

import {
  CreditUsageClient,
  CreditUsageResponse,
} from '@/composables/credits/useCreditUsage';
import { formatNumberWithCommas } from '@/utils';

import { formatCreditActivity } from './creditUsageFormatters';
import CreditUsageNameCell from './CreditUsageNameCell.vue';

const { isLoading, data } = defineProps<{
  data: CreditUsageResponse | null;
  isLoading: boolean;
}>();

const isInitialLoading = computed(() => isLoading && !data);

const rows = computed<CreditUsageClient[]>(() => data?.clients ?? []);

const columns: ColDef<CreditUsageClient>[] = [
  {
    headerName: 'Name',
    field: 'client',
    cellRenderer: CreditUsageNameCell,
    cellRendererParams: { kind: 'client' },
    flex: 2,
    minWidth: 200,
    sortable: true,
    resizable: false,
  },
  {
    headerName: 'Credits used',
    field: 'credits_used',
    flex: 1,
    minWidth: 140,
    sortable: true,
    resizable: false,
    valueFormatter: (params) => formatNumberWithCommas(params.value ?? 0),
  },
  {
    headerName: 'Last activity',
    field: 'last_activity',
    flex: 1,
    minWidth: 200,
    sortable: true,
    resizable: false,
    sort: 'desc',
    valueFormatter: (params) => formatCreditActivity(params.value),
  },
];
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
:deep() .empty-state-icon {
  @apply text-neutral-text-weak p-20 rounded-[36px];
  background: radial-gradient(
      136.96% 69.92% at 50.09% -12.42%,
      theme(colors.neutral.accent / 0.2) 0%,
      theme(colors.neutral.accent / 0) 100%
    ),
    linear-gradient(0deg, theme(colors.white), theme(colors.white));
}
</style>
